import { isNil, isString } from 'lodash';
import { forwardRef } from 'react';
import { RowContent } from '@shared/components/RowContent';
import { ButtonView } from '@shared/components/View';
import { grid } from '@shared/styles/grid';
import { useButtonBarContext } from './ButtonBarContext';
import { ButtonProps } from './ButtonProps';
import { useOnClickAsync } from './useOnClickAsync';
import styles from './Button.module.css';

/**
 * Lighthouse design-system button. This is the canonical "Lighthouse button". See the Lighthouse
 * playground (`/playground/button`) for the full set of variants, sizes, and states.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function ButtonWithRef(
  {
    variant = 'tertiary',
    size = 'large',
    icon,
    iconGridSize,
    iconOnly,
    label,
    numberOfLines,
    inactive,
    fullWidth,
    invalid,
    children,
    startContent,
    endContent,
    arrow,
    danger,
    engaged,
    className,
    style,
    disabled,
    loading: loadingProp,
    onClick: onClickProp,
    attributionId,
    bold,
    underline,
    vars,
    ...props
  },
  ref,
) {
  const [loading, onClick] = useOnClickAsync({
    onClick: onClickProp,
    loading: loadingProp,
    attributionId,
  });
  const buttonbar = useButtonBarContext();
  const buttonIconSize = isNil(iconGridSize) ? undefined : `${grid(iconGridSize)}px`;

  return (
    <ButtonView
      ref={ref}
      className={[styles.button, className]}
      style={style}
      disabled={disabled || loading || undefined}
      data-variant={buttonbar ? buttonbar.variant : variant}
      data-size={buttonbar ? buttonbar.size : size}
      data-buttonbar={buttonbar ? true : undefined}
      data-loading={loading || undefined}
      data-danger={danger || undefined}
      data-engaged={engaged || undefined}
      data-inactive={inactive || undefined}
      data-full-width={fullWidth || undefined}
      data-invalid={invalid || undefined}
      data-icon-only={iconOnly || undefined}
      data-arrow={arrow || undefined}
      data-bold={bold || undefined}
      data-underline={underline || undefined}
      aria-label={isString(label) ? label : undefined}
      onClick={onClick}
      vars={{ 'button-icon-size': buttonIconSize, ...vars }}
      {...props}
    >
      <RowContent
        icon={icon}
        iconGridSize={iconGridSize}
        label={iconOnly ? undefined : label}
        numberOfLines={numberOfLines}
        startContent={startContent}
        endContent={endContent}
      >
        {children}
      </RowContent>
    </ButtonView>
  );
});

Button.displayName = 'Button';
